import type { Experience, Role } from '@buakei/types';
import {
  BarChart3,
  LifeBuoy,
  NotebookPen,
  Activity,
  BookOpen,
  Briefcase,
  ClipboardCheck,
  FileCheck2,
  FolderLock,
  Gavel,
  GraduationCap,
  Home,
  Library,
  ListChecks,
  MessageSquareText,
  PenLine,
  Scale,
  Search,
  UserCog,
  type LucideIcon,
} from 'lucide-react';

export type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  stage?: 'preview' | 'later';
  /** Shown only to accounts holding this role. Experience only: the server re-checks. */
  requiresRole?: Role;
};

export const EXPERIENCE_META: Record<
  Experience,
  { label: string; home: string; icon: LucideIcon; scope: string }
> = {
  professional: {
    label: 'Professional',
    home: '/professional',
    icon: Scale,
    scope: 'Professional workspace',
  },
  learning: {
    label: 'Learning',
    home: '/learning',
    icon: GraduationCap,
    scope: 'Learning — private to you',
  },
  editorial: {
    label: 'Editorial',
    home: '/editorial',
    icon: ClipboardCheck,
    scope: 'Editorial — public legal sources only',
  },
  operations: {
    label: 'Operations',
    home: '/operations',
    icon: Activity,
    scope: 'Operations — no client content',
  },
};

/** The shared legal library: in every workspace, and the only item for accounts without one. */
export const LIBRARY_ITEM: NavItem = { to: '/library', label: 'Library', icon: Library };

export const NAV: Record<Experience, NavItem[]> = {
  professional: [
    { to: '/professional', label: 'Home', icon: Home },
    { to: '/professional/research', label: 'Research', icon: Search },
    { to: '/professional/memos', label: 'Research memos', icon: NotebookPen },
    { to: '/library/cite-check', label: 'Check citations', icon: FileCheck2 },
    LIBRARY_ITEM,
    { to: '/professional/drafting', label: 'Drafting', icon: PenLine },
    { to: '/professional/documents', label: 'Documents', icon: FolderLock, stage: 'preview' },
    { to: '/professional/matters', label: 'Matters & clients', icon: Briefcase, stage: 'later' },
    { to: '/professional/cases', label: 'Case intelligence', icon: Gavel, stage: 'later' },
  ],
  learning: [
    { to: '/learning', label: 'Home', icon: Home },
    { to: '/learning/tutor', label: 'Tutor', icon: MessageSquareText },
    LIBRARY_ITEM,
    { to: '/learning/subjects', label: 'Subjects', icon: BookOpen },
    { to: '/learning/cases', label: 'Case briefs', icon: Gavel },
    { to: '/learning/quizzes', label: 'Quizzes', icon: ListChecks },
    { to: '/learning/drafting', label: 'Practice drafting', icon: PenLine },
    { to: '/library/cite-check', label: 'Check citations', icon: FileCheck2 },
  ],
  editorial: [
    { to: '/editorial', label: 'Review queue', icon: ClipboardCheck },
    { to: '/editorial/quality', label: 'Quality review', icon: Scale },
    { to: '/editorial/received-law', label: 'Received law', icon: BookOpen },
    { to: '/editorial/doctrines', label: 'Doctrines', icon: NotebookPen },
    { to: '/editorial/sources', label: 'Source registry', icon: Library },
    LIBRARY_ITEM,
  ],
  operations: [
    { to: '/operations', label: 'Overview', icon: Activity },
    LIBRARY_ITEM,
    {
      to: '/operations/activity',
      label: 'Activity',
      icon: BarChart3,
      requiresRole: 'platform_admin',
    },
    { to: '/operations/access', label: 'Access', icon: UserCog, requiresRole: 'platform_admin' },
    { to: '/operations/support', label: 'Support', icon: LifeBuoy, requiresRole: 'platform_admin' },
  ],
};

export function experienceFromPath(pathname: string): Experience | null {
  const segment = pathname.split('/')[1];
  return segment === 'professional' ||
    segment === 'learning' ||
    segment === 'editorial' ||
    segment === 'operations'
    ? segment
    : null;
}
